import { memberEmailLocalPart } from "./member-email.js";
// Session scoping — the single client-side source of truth for "whose session
// is this, and which surface should it appear on" (#706).
//
// THE PROBLEM: on a daemon shared by several users, the chat sidebar's live
// Agents list (`workspaceState.agents`) shows EVERY user's sessions, so it
// floods and stops being scrollable. The fix moves OTHER users' personal
// sessions out of the chat sidebar into dedicated surfaces (daemon-detail for
// the per-daemon lens; Settings → Logs for the workspace-wide audit), while
// keeping MY sessions and the shared, channel-bound cybos where they belong.
//
// Pure + dependency-free so the predicates are unit-testable without a DOM and
// every surface (sidebar "mine" view, daemon-detail "daemon" view) shares the
// SAME definition of mine/daemon/channel-bound — mirrors the daemon-scopes.ts
// idiom (#705).

// The identity/binding fields this module reasons about. A structural subset of
// the agent row (plugins/agents/types.ts `Agent`) so callers can pass the live
// row straight through.
export interface SessionScopeFields {
  // The user id that LAUNCHED the session (server `agent_bindings.initiated_by`).
  // Null/absent on legacy rows the daemon couldn't attribute. NOTE: this is in the
  // OWNING daemon's LOCAL id space — for a session launched on ANOTHER daemon it is
  // a foreign local id (e.g. `c6cf5ecf`, or the synthetic `<id>@remote.local`) that
  // does NOT match any workspace member id. See `initiatedByEmail` for the bridge.
  initiatedBy?: string | null;
  // The initiator's canonical EMAIL — the stable cross-namespace identity carried
  // on every agent row (dispatcher `toAgentListRow` / `buildOfflineAgentRow`). Used
  // to resolve the owner's real name when `initiatedBy` (a foreign local id) can't.
  initiatedByEmail?: string | null;
  // The channel this agent posts into, when it is channel-bound. Null/absent for
  // a personal/ephemeral session that isn't wired to a shared channel.
  channelId?: string | null;
  // The daemon the session runs on.
  daemonId?: string | null;
  // A one-turn EPHEMERAL summon (@-mention / slash). It's transient — spawned for a
  // single turn and torn down when it completes, never continuable — so it must NEVER
  // appear in the chat sidebar's "mine" view (it would flicker in while inferring, then
  // vanish). The daemon carries this on the live list row (dispatcher `toAgentListRow`).
  // The daemon-detail AUDIT lens (filterByDaemon) deliberately STILL shows ephemerals —
  // it's the owner's "what ran on my machine" debug view — so this flag is honored ONLY
  // by the "mine" sidebar predicate, not by filterByDaemon.
  ephemeral?: boolean;
}

// The owner-identity fields on a session row, used to resolve a human display name
// for its group. A structural subset of the agent row.
export interface SessionOwnerIdentity {
  initiatedBy?: string | null;
  initiatedByEmail?: string | null;
}

// A workspace member as far as owner-name resolution cares: an id, an optional
// display name, and an optional email for the cross-daemon fallback.
export interface OwnerMemberLike {
  userId: string;
  name?: string | null;
  email?: string | null;
}

// The friendly label shown when a session's owner resolves to neither a known
// member id nor a known member email — better than leaking a raw local id / opaque
// hash (e.g. `c6cf5ecf`) or a bare "unknown".
export const UNRESOLVED_OWNER_LABEL = "Member (other daemon)";

// A member's human display name: real name, else the email local-part, else a
// short id stub.
function memberDisplayName(m: OwnerMemberLike): string {
  return m.name ?? (memberEmailLocalPart(m) || m.userId.slice(0, 8));
}

// Resolve a session's OWNER to a human display name (cross-daemon owner-name
// resolution). Tries, in order:
//   1. `initiatedBy` → a workspace member by id (same-daemon / already-bridged id),
//   2. `initiatedByEmail` → a workspace member by email (CASE-INSENSITIVE) — this
//      is what re-attributes a session launched on ANOTHER daemon, whose
//      `initiatedBy` is a foreign local id (e.g. `c6cf5ecf` / `a4e9d6d4@remote.local`)
//      that never matches a member id,
//   3. a friendly fallback label — NEVER a raw id or "unknown".
// Pure + dependency-free so it is unit-testable and shared by both the group header
// and the group sort ordering, keeping them in lockstep.
export function resolveOwnerName(
  session: SessionOwnerIdentity,
  members: readonly OwnerMemberLike[],
  fallbackLabel: string = UNRESOLVED_OWNER_LABEL,
): string {
  const member = resolveOwnerMember(session, members);
  return member ? memberDisplayName(member) : fallbackLabel;
}

// The WORKSPACE MEMBER a session's owner resolves to, or undefined when neither
// arm matches (a foreign daemon-local id, a synthetic `@remote.local` row, a
// member who has since left). Split out of resolveOwnerName — which is now a thin
// display wrapper over it, so name and member can never disagree — because a
// surface that wants to REACH the owner (open their DM) needs the id, and a
// resolved NAME is the only proof there is a real person behind it. A caller that
// gets undefined must offer no owner-directed action at all rather than fabricate
// one from a raw id.
export function resolveOwnerMember<M extends OwnerMemberLike>(
  session: SessionOwnerIdentity,
  members: readonly M[],
): M | undefined {
  const id = session.initiatedBy;
  if (id) {
    const byId = members.find((m) => m.userId === id);
    if (byId) return byId;
  }
  const email = session.initiatedByEmail;
  if (email) {
    const lower = email.toLowerCase();
    const byEmail = members.find((m) => !!m.email && m.email.toLowerCase() === lower);
    if (byEmail) return byEmail;
  }
  return undefined;
}

// Which surface a session list is rendered on:
//   mine      — the chat sidebar, filtered to the current user (+ shared cybos).
//   daemon    — daemon-detail, ALL users on ONE daemon (grouped by owner).
//   workspace — the workspace-wide audit (Settings → Logs, #704). Kept here so
//               the scope union is exhaustive; this module does no filtering for
//               it (the Logs tab owns the firehose) but the type documents it.
export type SessionScope = "mine" | "daemon" | "workspace";

// ─── THE VIEWER'S IDENTITY (the id-namespace bridge) ──────────────────────
//
// WHO THE VIEWER IS, for every ownership predicate below. Historically a bare user
// id — still accepted so an id-only call site keeps compiling — but a bare id CANNOT
// decide ownership on a shared daemon, which is the bug this type exists to make
// unrepresentable. Pass the user OBJECT (`authState.user`) so the email arm fires.
//
// MEASURED, not hypothetical (the owner's daemon, cybo `Linkedin Guy`): ONE human has
// TWO ids on the SAME daemon, and which one lands in `agent_bindings.initiated_by`
// depends on which door the row came through.
//   • an RPC row (`cyborg:spawn_cybo`) is stamped with the relay/CLOUD id — the same
//     id `authState.user.id` carries, so the id arm matches and the owner's own
//     sessions always resolved;
//   • a DM-forward row (`cybo_dm_forward` → handleDm → ensureDefaultSession) is
//     stamped with the daemon-LOCAL, email-derived id, because bootstrap remaps the
//     sender to this daemon's own user row (local membership is keyed on it).
// So a TEAMMATE's own default DM session — `dmdef-<cybo>-<localId>` — never matched
// their own `authState.user.id`, and `filterMine` DISCARDED THE ROW THE SERVER HAD
// JUST DELIVERED TO THEM. Downstream, `defaultDmSessionForCybo` then found nothing,
// the agent route fell to its session-less branch, and the live "…is thinking"
// indicator (gated on `isTurnActive(agent.agentId)`) had no id to watch: the
// teammate's reply appeared out of dead air while the owner's worked perfectly.
//
// The bridge is the EMAIL that is ALREADY ON EVERY ROW the daemon sends — dispatcher's
// `toAgentListRow` resolves `initiatedBy` through its own users table into
// `initiatedByEmail` precisely so a foreign id space can be crossed, and the relay
// passes it through verbatim. It is also exactly what the SERVER matches on:
// storage.ts's `resolveCanonicalPairSession` (the #810 id-OR-email rule) and
// message-router's `bindingOwnedBy`. Client and server now answer "is this session
// mine" the same way, so a row the server hands over can no longer be thrown away by
// its own owner. Nothing new is sent over the wire; this reads a field that was
// already there and previously only fed a display name (`resolveOwnerName`).
//
// NOT A WIDENING: the email arm can only ever match the VIEWER'S OWN address, so it
// strictly SHRINKS the "someone else's" set by the viewer's own rows. It changes
// nothing about what the server sends or who a stream is scoped to (the relay gates
// live frames on `privateToEmail`, untouched here) — it is purely which of the rows
// already in this client's roster the viewer is allowed to see as theirs.
// `canonicalIds` is EVERY id that names the viewer — the relay now puts its own
// `sameHumanIds(user.id, user.email)` on the auth response verbatim, so the client and
// the server read ONE definition of "the same human" rather than two that drift. It
// INCLUDES `id`; an ARRAY rather than a second scalar so a human with more than one
// verified address needs no wire change.
// OPTIONAL on purpose: an older relay simply omits it and every predicate below
// degrades to exactly today's single-id behavior rather than failing to parse.
export type ViewerIdentity =
  | string
  | { id?: string | null; canonicalIds?: readonly string[] | null; email?: string | null }
  | null
  | undefined;

// EVERY id that provably names the viewer — the client twin of the server's
// `sameHumanIds`. Both entries come from the viewer's OWN authenticated auth
// response, and the second is a pure function of their own verified address, so this
// is same-human by construction: a DIFFERENT human's id can never appear here.
function viewerIds(viewer: ViewerIdentity): string[] {
  if (!viewer) return [];
  if (typeof viewer === "string") return viewer ? [viewer] : [];
  const ids = new Set<string>();
  if (viewer.id) ids.add(viewer.id);
  // Tolerates a relay that OMITS `id` from the set as well as one that includes it.
  for (const id of viewer.canonicalIds ?? []) if (id) ids.add(id);
  return [...ids];
}

// Does this id name the VIEWER THEMSELVES? The bare-id predicate every raw
// `x === authState.user.id` comparison should be — a human holds TWO ids (see
// ViewerIdentity) and which one a server payload carries is decided by the TRANSPORT
// that produced the row, not by the person. A raw compare therefore drops the SAME
// human's own traffic whenever it arrives stamped with their other id: an agent's DM
// reply, a `cybo_mention_notice`, a `/catchup` result, their own cybos, a channel
// they just created.
//
// NOT A WIDENING and safe on a MESSAGE-READ path: this only ever adds the viewer's
// own second id, so it strictly SHRINKS the "someone else's" set by the viewer's own
// rows. A blank/absent id never matches anything.
export function isMe(id: string | null | undefined, viewer: ViewerIdentity): boolean {
  return !!id && viewerIds(viewer).includes(id);
}

// A REAL address, lowercased, else undefined. Twin of the server's
// `canonicalOwnerEmail` / the `initiated_by_email` guards in storage.ts's SQL: a blank
// is not an identity, and neither is the synthetic `<id>@remote.local` row `ensureUser`
// plants for a cloud guest — without that exclusion two DIFFERENT placeholder-emailed
// humans could compare "equal" on a shape rather than on an identity. NOT theoretical:
// a CLOUD-id row's `initiatedByEmail` arrives as EXACTLY that placeholder (the daemon
// derives it from its own users table, and a cloud guest's local row has nothing else),
// so every cloud-spawned session in the workspace carries one.
function realEmail(email: string | null | undefined): string | undefined {
  const e = email?.trim().toLowerCase();
  return !e || e.endsWith("@remote.local") ? undefined : e;
}

function viewerEmail(viewer: ViewerIdentity): string | undefined {
  return typeof viewer === "string" || !viewer ? undefined : realEmail(viewer.email);
}

// Is this session the VIEWER'S OWN? Their id (either namespace's — whichever this row
// happens to carry), OR their real email against the row's stamped `initiatedByEmail`.
// See ViewerIdentity for why one arm is not enough.
export function isMine(session: SessionScopeFields, viewer: ViewerIdentity): boolean {
  if (isMe(session.initiatedBy, viewer)) return true;
  const mine = viewerEmail(viewer);
  return !!mine && realEmail(session.initiatedByEmail) === mine;
}

// May the viewer see and answer this session's permission prompts? Twin of the server's
// answersSessionPermissions: the session's initiator or the workspace owner, never an admin.
export function answersSessionPermissions(
  session: SessionScopeFields,
  viewer: ViewerIdentity,
  role: string | null | undefined,
): boolean {
  return role === "owner" || isMine(session, viewer);
}

// Is this a SHARED, channel-bound agent? An agent wired to a channel posts into
// the shared workspace, so it belongs wherever the channel is — it must NOT be
// hidden from the sidebar just because someone else launched it. This is the
// conservative side of the #706 trade-off: a channel binding keeps a session
// visible to the whole team.
export function isChannelBound(session: SessionScopeFields): boolean {
  return session.channelId != null && session.channelId !== "";
}

// The "moved out" set: another user's PERSONAL session (not mine, not
// channel-bound). Conservative on attribution — a row with NO `initiatedBy`
// (legacy/unattributable) is NOT treated as "someone else's", so it stays
// visible in the sidebar rather than silently vanishing. This is the single
// predicate that decides what LEAVES the sidebar; everything else stays.
export function isOthersPersonalSession(
  session: SessionScopeFields,
  viewer: ViewerIdentity,
): boolean {
  if (isChannelBound(session)) return false; // shared → stays
  if (session.initiatedBy == null || session.initiatedBy === "") return false; // unattributable → stays
  // isMine, not a bare id compare: the same human owns rows under BOTH a cloud and a
  // daemon-local id (see ViewerIdentity), and an id-only test called their own DM
  // session "someone else's" and hid it from them.
  return !isMine(session, viewer);
}

// Does this session belong in the chat sidebar's filtered "mine" view? Stays
// unless it's an EPHEMERAL one-turn summon (transient, never continuable — hidden
// even from its own initiator) OR specifically another user's PERSONAL session — so
// mine, shared channel-bound cybos (the trade-off), AND unattributable legacy rows
// all stay visible; only ephemerals and other users' personal sessions move to the
// daemon/workspace surfaces. The daemon-detail AUDIT lens (filterByDaemon) is
// unaffected and still shows ephemerals.
export function belongsInMineSidebar(session: SessionScopeFields, viewer: ViewerIdentity): boolean {
  if (session.ephemeral === true) return false; // transient one-turn summon → never in the sidebar
  return !isOthersPersonalSession(session, viewer);
}

// Filter a list to the chat sidebar's "mine" view (mine + shared channel cybos).
// NOTE: this filters by USER only — never by daemon. The "my sessions" view is
// deliberately CROSS-DAEMON: my sessions on every (online) daemon aggregate into
// one list, each row tagged with its daemon (see remoteDaemonLabel). Scoping the
// sidebar to a single daemon was a regression — my sessions on other machines
// would silently vanish until I switched daemons.
export function filterMine<T extends SessionScopeFields>(
  sessions: readonly T[],
  viewer: ViewerIdentity,
): T[] {
  return sessions.filter((s) => belongsInMineSidebar(s, viewer));
}

// Why each row is or is not under the sidebar's "Sessions" (cold-start trace, counts only).
// `shown` equals ChannelSidebar's `cliSessionRows` length: the collapse never drops a non-cybo row.
export function sessionsSectionCounts(
  sessions: readonly (SessionScopeFields & { cyboId?: string | null })[],
  viewer: ViewerIdentity,
): {
  rows: number;
  shown: number;
  ephemeral: number;
  notMine: number;
  cybo: number;
  viewerIds: number;
  shownPerDaemon: Record<string, number>;
} {
  const out = { rows: sessions.length, shown: 0, ephemeral: 0, notMine: 0, cybo: 0 };
  const shownPerDaemon: Record<string, number> = {};
  for (const s of sessions) {
    if (s.ephemeral === true) out.ephemeral += 1;
    else if (isOthersPersonalSession(s, viewer)) out.notMine += 1;
    else if (s.cyboId != null) out.cybo += 1;
    else {
      out.shown += 1;
      const d = s.daemonId ?? "none";
      shownPerDaemon[d] = (shownPerDaemon[d] ?? 0) + 1;
    }
  }
  return { ...out, viewerIds: viewerIds(viewer).length, shownPerDaemon };
}

// ─── Per-cybo collapse (knowledge/83, Phase 4) ────────────────────────────

// The identity fields the per-cybo collapse reasons about, on top of the scope
// fields. A structural subset of the agent row (plugins/agents/types.ts `Agent`).
export interface CyboSessionFields extends SessionScopeFields {
  // The agent-binding id. `dmdef-<cybo>-<user>` marks a default session.
  agentId: string;
  // The cybo this session belongs to, or null for a non-cybo (plain provider) agent.
  cyboId?: string | null;
  // Server flag for the recipient-owned DEFAULT session. May be absent on the
  // daemon LIVE path (toAgentListRow historically omitted it), so callers fall
  // back to the `dmdef-` id prefix below.
  isDefault?: boolean;
}

// Is this a cybo's canonical DEFAULT session (knowledge/83)? True when the server
// flagged it `is_default`, OR — the live-path fallback, when the daemon's list row
// omits the flag — when the agent id has the `dmdef-<cybo>-<user>` shape. Mirrors
// the same predicate the agent route uses to pick DefaultSessionView.
export function isDefaultCyboSession(s: { agentId: string; isDefault?: boolean }): boolean {
  return s.isDefault === true || s.agentId.startsWith("dmdef-");
}

// Does a DEFAULT session (isDefaultCyboSession above) still render the read-only
// DM-thread view (DefaultSessionView) on the agent route, instead of the full
// agent stream + composer every other session gets? True ONLY for a session with
// NO live agent — the server's synthetic `dmdef-<cybo>-<user>` id, minted before
// any real binding exists (knowledge/83). A PROMOTED default (`isDefault` flipped
// true on a REAL, resumable agent binding — e.g. a cybo bound as the workspace's
// default) keeps its ORIGINAL uuid `agentId`, has a live timeline, and must
// render exactly like a normal session. So this is deliberately narrower than
// isDefaultCyboSession: it keys on the `dmdef-` id prefix itself, not the
// `isDefault` flag.
export function shouldRenderDefaultDmView(s: { agentId: string; cyboId?: string | null }): boolean {
  return !!s.cyboId && s.agentId.startsWith("dmdef-");
}

// Collapse each cybo's PERSONAL sessions down to just its default one — the "one
// visible session per cybo" the sidebar wants (knowledge/83, Phase 4). A cybo has
// one canonical conversation: the recipient-owned default session (a DM thread).
// Its other personal sessions (ad-hoc interactive spawns, mention summons that
// outlived their archive) are sidebar noise.
//
// SAFE BY CONSTRUCTION — the collapse for a cybo fires ONLY when that cybo has a
// default row present. A cybo with no default yet (pre-Phase-2 DM history, before
// the backfill mints its `dmdef-` binding) keeps ALL its rows rather than
// vanishing. Channel-bound sessions are shared team work and are never collapsed;
// non-cybo sessions pass through untouched. Order is preserved.
export function collapseToDefaultPerCybo<T extends CyboSessionFields>(sessions: readonly T[]): T[] {
  // Which cybos have a default among their personal (non-channel-bound) rows?
  const cybosWithDefault = new Set<string>();
  for (const s of sessions) {
    if (s.cyboId && !isChannelBound(s) && isDefaultCyboSession(s)) {
      cybosWithDefault.add(s.cyboId);
    }
  }
  if (cybosWithDefault.size === 0) return [...sessions];
  return sessions.filter((s) => {
    // Untouched: non-cybo, channel-bound (shared), or a cybo with no default yet.
    if (!s.cyboId || isChannelBound(s) || !cybosWithDefault.has(s.cyboId)) return true;
    // This cybo has a default → keep only its default row(s).
    return isDefaultCyboSession(s);
  });
}

// How many personal sessions the per-cybo collapse would hide — drives an optional
// "N older sessions collapsed" affordance so the fold reads as intentional, not a
// silent disappearance. Just the count delta of the collapse.
export function countCollapsedByDefault(sessions: readonly CyboSessionFields[]): number {
  return sessions.length - collapseToDefaultPerCybo(sessions).length;
}

// ─── DM addressing: ONE address per agent ─────────────────────────────────

// The chat URL id for a session row. A cybo is addressed by its CYBO id — never
// by the id of whichever RUN happens to be serving the conversation right now. A
// plain provider/CLI session has no cybo and IS its own address.
//
// THE BUG THIS DELETES: `pickDmRepresentative` used to answer "which run did the
// human mean?" with newest-createdAt-wins (tie-broken by agentId), and the answer
// became the sidebar row's href. So a fresh spawn silently re-pointed the row, and
// the agent the founder DMs every day addressed a different run each time — one
// agent read as a stream of strangers. That question has no answer and is not a
// thing to tune: the human addresses the AGENT, and which run serves it is an
// implementation detail he must never perceive.
//
// Every entry point builds its href from here (sidebar DM row, Start chat, the
// mobile Agents tab) and the push deep-link already carries the cybo id
// (`agent-dm-reply-push.ts`: `url: …/agent/${payload.fromId}`), so all four land
// on the SAME URL by construction rather than agreeing by luck. The agent route
// resolves a cybo id to that cybo's DEFAULT session — the server's canonical,
// per-user DM binding — and, when there is none, renders the cybo↔user DM thread
// itself, which spans every run. Neither step guesses.
export function dmAddress(session: { agentId: string; cyboId?: string | null }): string {
  return session.cyboId ?? session.agentId;
}

// The session a not-run verdict is about: the agent it names, else the viewer's DM with the cybo.
export function notRunSessionId<T extends CyboSessionFields>(
  verdict: { agentId?: string | null; cyboId: string | null },
  mySessions: readonly T[],
): string | undefined {
  if (verdict.agentId) return verdict.agentId;
  return verdict.cyboId ? defaultDmSessionForCybo(mySessions, verdict.cyboId)?.agentId : undefined;
}

// The cybo↔person DM as one conversation, whichever view (DM thread or session stream) shows it.
export function agentDmConversationKey(workspaceId: string, cyboId: string): string {
  return `agent-dm:${workspaceId}:${cyboId}`;
}

// The URL id that opens THIS session row. A channel-bound run is not the cybo's DM —
// the cybo address resolves to its default DM session — so it opens by its own id.
export function sessionAddress(
  session: { agentId: string; cyboId?: string | null } & SessionScopeFields,
): string {
  return isChannelBound(session) ? session.agentId : dmAddress(session);
}

// ─── DM-row derivation (the "duplicate Ethan" fix) ────────────────────────

// Map each cybo to ONE of its sessions — the row a DM LIST renders (live status
// dot, pending-permission count, the archive ×, "does it have a live session at
// all"). `collapseToDefaultPerCybo` folds a cybo's PERSONAL sessions to its
// default but deliberately KEEPS every channel-bound row (a channel reply is
// shared team work) — so a cybo that also replied in N channels still had N+1 rows
// survive as "DM" rows, each rendering as a duplicate. This is the DM-surface rule
// on top of that:
//   1. A channel-bound session is never a candidate — a channel reply is not a
//      direct conversation, however active the cybo is there.
//   2. At most ONE session per cyboId survives: the cybo's DEFAULT/DM session when
//      it has one, else the first non-channel-bound row seen.
//   3. A cybo with ONLY channel-bound sessions (never DM'd) has NO entry here — it
//      is reachable from the channel it's active in, not as a phantom DM.
//
// DISPLAY ONLY — it is deliberately NOT an address (see `dmAddress`). The old
// newest-createdAt tie-break existed because this map WAS the href; now that the
// href is the cybo id, which run backs the row is cosmetic and a re-pick can no
// longer move the human into a different conversation. So the tie-break is deleted
// rather than tuned, and `createdAt` is no longer an input at all.
//
// Unread safety: the cyboId-keyed DM badge (`badgeCount`/`hasBadge` via
// `canonicalAgentDmPeerId`) is only ever incremented by a genuine DM message
// (peerId = cyboId, app.svelte.ts `handleIncomingDmFromPeer`) — channel activity
// bumps the CHANNEL's own unread instead (`notifyForIncoming`, keyed by
// `msg.channelId`). So dropping a channel-bound row from this derivation can never
// hide a real DM-unread signal; it only stops the same real badge from rendering
// once per duplicate.
export function dmSessionByCybo<T extends CyboSessionFields>(
  sessions: readonly T[],
): Map<string, T> {
  const result = new Map<string, T>();
  for (const s of sessions) {
    if (!s.cyboId || isChannelBound(s)) continue;
    const chosen = result.get(s.cyboId);
    // First row wins (Map insertion order = list order), upgraded when a better-ranked
    // row for the cybo turns up later in the list.
    if (!chosen || dmRowRank(s) > dmRowRank(chosen)) result.set(s.cyboId, s);
  }
  return result;
}

// Not only display: the row's ⋮ End session acts on its agentId. So rank like
// defaultDmSessionForCybo (the route): a `dmdef-` row the server demoted (isDefault not true,
// default by its prefix alone) loses to the real session promoted over it; one still flagged wins.
function dmRowRank(s: CyboSessionFields): number {
  if (!isDefaultCyboSession(s)) return 0;
  if (!s.agentId.startsWith("dmdef-")) return 2;
  return s.isDefault === true ? 3 : 1;
}

// The chat sidebar's Direct Messages rows for cybos: one dmSessionByCybo entry per
// cybo, as a flat list (dmSessionByCybo's own doc has the full rule).
export function dmRowsForCybos<T extends CyboSessionFields>(sessions: readonly T[]): T[] {
  return [...dmSessionByCybo(sessions).values()];
}

// DOES THIS CYBO ALREADY HAVE A DM CONVERSATION WITH THE VIEWER? The one predicate
// every "Start chat" decides on, because it decides whether a conversation gets
// CREATED — and there are two of those buttons (the Agents pane's cybo card and the
// cybo profile page, which the create-agent wizard's `?chat=1` deep link also fires).
//
// WHY IT IS ONE FUNCTION. The chat is addressed by the CYBO id (`dmAddress`) and the
// spawn's returned agentId is deliberately discarded, so a spawn that runs when a
// conversation already exists does not send the human anywhere new — it just leaves
// another DM binding behind. Measured on one daemon: a single cybo accumulated four
// separate DM bindings whose turns split 64 / 5 / 1 / 16, i.e. one conversation the
// human experiences as one, fragmented across four sessions on disk. Two call sites
// each re-deriving this from `filterMine`/`dmSessionByCybo` is how one of them ends
// up asking a subtly different question than the other.
//
// WHAT THE CALLER MUST PASS. `sessions` should be the roster read from the SERVER at
// the moment the decision is made, not a cached one: `workspaceState.agents` is empty
// on a cold click, short while a daemon is still answering the fan-out, and stale
// after a reconnect — and every one of those reads as "no conversation". A cheap
// in-memory ask up front is fine as a fast path; the ask that GATES the spawn is not.
// WHAT COUNTS AS "ALREADY HAS ONE" — and the bug this line fixes.
//
// A record-only `dmdef-<cybo>-<user>` row is a CONVERSATION, not a SESSION: it is
// minted at cybo CREATION by startFirstRunGreeting, has no agent behind it, and can
// never gain one. Counting it here made this predicate permanently true from the
// moment a cybo existed — so all four "Start chat" surfaces short-circuited to a
// plain `goto` and the spawn below them was unreachable code. No cybo could ever
// get its first real session that way, for any user. Measured 2026-09-02: a tester
// with six agents, none of which could reach the agent-settings menu, because the
// route only mounts AgentComposer for a session with a live agent.
//
// `shouldRenderDefaultDmView` is deliberately reused rather than a fresh
// `startsWith("dmdef-")`: it is the SAME predicate the agent route uses to decide it
// must fall back to the DM-thread view, so "the route would render the read-only
// surface" and "a spawn is needed" can never drift apart. The server agrees from its
// own side — getPromotableDmBinding excludes `dmdef-%` for exactly this reason.
//
// The dedup this guard exists for is intact: a cybo with a REAL binding still
// answers true and is not re-spawned. Even if promotion never fires, the second
// click sees that real binding, so the worst case is one extra binding, never the
// four-way fragmentation (turns split 64 / 5 / 1 / 16) that motivated this function.
export function hasDmSession(
  sessions: readonly CyboSessionFields[],
  viewer: ViewerIdentity,
  cyboId: string,
): boolean {
  const real = filterMine(sessions, viewer).filter((s) => !shouldRenderDefaultDmView(s));
  return dmSessionByCybo(real).has(cyboId);
}

// An agent DM'd `viewer` and the roster has no real session with it, though the agent spoke
// on one (greeting / setup done turn): the roster is stale and worth one re-read.
export function rosterMissesAgentSession(
  sessions: readonly CyboSessionFields[],
  viewer: ViewerIdentity,
  msg: { fromType: string; fromId: string; toId?: string | null; parentId?: string | null },
): boolean {
  if (msg.fromType !== "agent" || msg.parentId || !isMe(msg.toId, viewer)) return false;
  return !hasDmSession(sessions, viewer, msg.fromId);
}

// The session that SERVES a cybo's DM conversation, for a route addressed by cybo
// id (`dmAddress`). Deliberately narrower than dmSessionByCybo above: only the
// cybo's DEFAULT session — the server's canonical, per-user DM binding, either the
// minted `dmdef-<cybo>-<user>` row or the uuid it was promoted onto — qualifies.
// There is no "closest run" fallback ON PURPOSE: an address must resolve to the
// same conversation every time or it is not an address. A cybo with runs but no
// default returns undefined, and the caller renders the cybo↔user DM thread by
// cybo id instead, which is the conversation itself and spans every run.
//
// TIE-BREAK — the `dmdef-` row FIRST, then list order. "Exactly one default per pair"
// is an invariant the server enforces on WRITE (setAgentBindingDefault's demotion),
// and its demotion query is scoped by `initiated_by = ?` — so it structurally cannot
// see a sibling default of the SAME human stamped with their OTHER id (see
// ViewerIdentity), and a pair CAN legitimately show two defaults in the roster right
// now (measured on the owner's daemon). With a bare `.find` the row this page watches
// was then decided by roster order — i.e. by luck — and half the time it picked the
// row the daemon does NOT route the turn to, which is a silent indicator again.
// So mirror the SERVER's own answer: `resolveCanonicalPairSession(requireDefault)`
// orders `CASE WHEN agent_id = 'dmdef-<cybo>-<human>' THEN 0 ELSE 1` first, so the
// dmdef row is the session the DM lands on, and therefore the one to watch. Agreeing
// with the router by construction beats agreeing with it by luck.
export function defaultDmSessionForCybo<T extends CyboSessionFields>(
  sessions: readonly T[],
  cyboId: string,
  computers: DefaultSessionComputers = {},
): T | undefined {
  const defaults = preferOnlineDefaults(
    sessions.filter((s) => s.cyboId === cyboId && !isChannelBound(s) && isDefaultCyboSession(s)),
    computers,
  );
  // A PROMOTED default outranks the `dmdef-` row — the tie-break above is right
  // about mirroring the server, and wrong about which row the server picks.
  //
  // `resolveCanonicalPairSession(requireDefault)` orders on
  // `agent_id = 'dmdef-<cybo>-<human>'` for the HUMAN IT WAS CALLED WITH. It puts
  // that row first because, for that human, it IS the canonical one. It does not
  // say "any dmdef row beats any real session". The client's copy dropped the
  // human from the comparison and kept only the prefix, so it promotes a `dmdef-`
  // row belonging to the person's OTHER id — the exact sibling the comment above
  // already warns the server's demotion cannot see.
  //
  // Measured on the owner's daemon, for one cybo:
  //
  //   dmdef-cybo_20679b9c-…-e3ec328c…  provider_session NULL  is_default 0
  //   8f10b630-…                       provider_session set   is_default 1
  //
  // The server had already DEMOTED the dmdef row. The client picked it anyway,
  // purely on the prefix, and the route then rendered the read-only DM view for a
  // conversation whose real session was live and mid-turn: no model / mode /
  // thinking controls, and a "Start" button that could not work because the pair
  // already had an active run.
  //
  // So trust the server's own answer where it exists. `isDefault === true` is that
  // answer, and only a REAL binding can carry it meaningfully — a `dmdef-` row
  // reaches isDefaultCyboSession through the id prefix alone, which is why the
  // flag has to be read on a non-dmdef row to mean anything here.
  //
  // A TIE-BREAK, NOT A FILTER, deliberately: a pair whose only row IS the `dmdef-`
  // one still resolves to it (the line below is untouched), so a cybo that has
  // never been promoted keeps addressing exactly as before. This only stops a row
  // the server has already demoted from outranking the one it promoted.
  // Trust the server's flag on the dmdef row: demoted (false) ⇒ the promoted real
  // session is the conversation; still flagged ⇒ the daemon may route the pair's
  // turns there (a teammate's cloud-id spawn and local-id dmdef row), so it stays.
  const dmdef = defaults.find((s) => s.agentId.startsWith("dmdef-"));
  const promoted = defaults.find((s) => !s.agentId.startsWith("dmdef-") && s.isDefault === true);
  if (promoted && dmdef?.isDefault !== true) return promoted;
  return dmdef ?? defaults[0];
}

// What the route knows about the computers behind a cybo's default sessions (CYBORG-1207).
export interface DefaultSessionComputers {
  // true online, false offline, null when the daemon is not known to this client.
  isOnline?: (daemonId: string) => boolean | null;
  // The session this route resolved to last, kept while it stays eligible.
  lastUsedAgentId?: string | null;
}

// A person can hold one default per computer (a sleeping Mac re-sends its rows on every
// wake). When one is online, offline ones drop out; online ones come first, the one last
// used leading. Otherwise the list is returned as it came.
function preferOnlineDefaults<T extends CyboSessionFields>(
  defaults: T[],
  computers: DefaultSessionComputers,
): T[] {
  const { isOnline, lastUsedAgentId } = computers;
  if (defaults.length < 2 || !isOnline) return defaults;
  const online = defaults.filter((s) => s.daemonId && isOnline(s.daemonId) === true);
  if (online.length === 0) return defaults;
  const unknown = defaults.filter((s) => !s.daemonId || isOnline(s.daemonId) === null);
  const last = online.find((s) => s.agentId === lastUsedAgentId);
  const first = last ? [last, ...online.filter((s) => s !== last)] : online;
  return [...first, ...unknown];
}

// Whether a session runs on a daemon OTHER than the viewer's own/current one —
// i.e. genuinely hosted on a different machine. This is what "Remote" MUST mean.
//
// Do NOT use the server's `daemonLocal` flag for this: it is TRUE only while the
// agent is live in the responding daemon's MEMORY (`toAgentListRow`: `daemonLocal:
// live != null`), so an idle/dead agent on the user's OWN machine reports
// `daemonLocal: false` and was falsely tagged "Remote" (the Seb / "Rick" bug).
// Durable session→daemon attribution (`agent.daemonId`, #1299/#1300) lets us
// compare the OWNING daemon to the viewer's own daemon instead.
//
// Conservative — never guess "remote":
//  - `daemonId` null/unknown (not yet attributed) → NOT remote (stays local, so
//    an unattributed row never spuriously reads as another machine's).
//  - `viewerDaemonId` unknown (no own/current daemon resolved) → NOT remote
//    (nothing to be remote relative to; avoids over-badging cloud-only views).
//  - `daemonId` === the viewer's own/current daemon → NOT remote (idle-but-local).
//  - otherwise (attributed to a DIFFERENT daemon) → remote.
//
// Callers pass the daemon the viewer treats as "theirs": an explicitly selected
// daemon when the view is scoped to one (every visible row is then local to it by
// construction), else their effective default daemon.
export function isRemoteSession(
  session: { daemonId?: string | null },
  viewerDaemonId: string | null | undefined,
): boolean {
  const owner = session.daemonId;
  if (!owner || !viewerDaemonId) return false;
  return owner !== viewerDaemonId;
}

// The daemon tag for a session row in the cross-daemon "mine" view, or null when
// the session runs on the viewer's own/current daemon (local rows need no badge).
// Remote is decided by `isRemoteSession` (owning daemon vs the viewer's own),
// NOT the memory-liveness `daemonLocal` flag. `nameFor` resolves the owning
// daemon id → a friendly label, falling back to "Remote" when it's unknown. This
// is what makes the aggregated list legible: without it a remote session is
// indistinguishable from a local one and the list reads as "one daemon".
export function remoteDaemonLabel(
  session: { daemonId?: string | null },
  viewerDaemonId: string | null | undefined,
  nameFor: (daemonId: string) => string | null | undefined,
): string | null {
  if (!isRemoteSession(session, viewerDaemonId)) return null;
  const name = session.daemonId ? nameFor(session.daemonId) : null;
  return name && name.trim() ? name : "Remote";
}

// ─── Daemon scope (daemon-detail) ─────────────────────────────────────────

// Sessions on ONE daemon, ALL users (the daemon owner's "what ran on my
// machine" lens). Same predicate as DaemonDetail's existing activeSessions.
export function filterByDaemon<T extends SessionScopeFields>(
  sessions: readonly T[],
  daemonId: string,
): T[] {
  return sessions.filter((s) => s.daemonId === daemonId);
}

// A group of one user's sessions on a daemon — the daemon-detail view leads each
// group with the owner's avatar + name (not cybo/cwd), so the daemon owner
// recognizes whose sessions these are at a glance.
export interface SessionOwnerGroup<T> {
  // The launching user id, or null for unattributable/legacy rows.
  userId: string | null;
  sessions: T[];
}

// Group a daemon's sessions by their owner (`initiatedBy`). MY group sorts first
// (so the owner sees their own sessions at the top), then other users
// alphabetically by their resolved display name, then the unattributable group
// last. `ownerNameOf` resolves a group's OWNER IDENTITY (id + email) → display name
// for the stable ordering — email-aware so a cross-daemon owner sorts by their real
// name, not a foreign local id; null-id rows collate to the end. It reads the
// group's representative session (every group has ≥1), so it sees `initiatedByEmail`
// and stays in lockstep with the header label (both call `resolveOwnerName`).
export function groupByOwner<T extends SessionScopeFields>(
  sessions: readonly T[],
  currentUserId: string | undefined,
  ownerNameOf: (owner: SessionOwnerIdentity) => string,
): SessionOwnerGroup<T>[] {
  const groups = new Map<string | null, T[]>();
  for (const s of sessions) {
    const key = s.initiatedBy != null && s.initiatedBy !== "" ? s.initiatedBy : null;
    const bucket = groups.get(key);
    if (bucket) bucket.push(s);
    else groups.set(key, [s]);
  }
  return [...groups.entries()]
    .map(([userId, list]) => ({ userId, sessions: list }))
    .sort((a, b) => {
      // Mine first.
      const aMine = a.userId != null && a.userId === currentUserId;
      const bMine = b.userId != null && b.userId === currentUserId;
      if (aMine !== bMine) return aMine ? -1 : 1;
      // Unattributable last.
      if ((a.userId === null) !== (b.userId === null)) return a.userId === null ? 1 : -1;
      if (a.userId === null || b.userId === null) return 0;
      // Otherwise alphabetical by resolved display name (id- or email-bridged).
      return ownerNameOf(a.sessions[0]).localeCompare(ownerNameOf(b.sessions[0]));
    });
}

// CYBO-1846 S5: Clone shows as trunk (the server answers), hidden ONLY where the server refuses on
// DM grounds: an owner/admin on someone else's session the server does not PROVE is no DM (its
// fail-closed `dmSession`, attributed or not, as the relay reads the mirror).
export function mayCloneSession(
  session: SessionScopeFields & { dmSession?: boolean },
  viewer: ViewerIdentity,
  role: string | null | undefined,
): boolean {
  if (isMine(session, viewer) || (role !== "owner" && role !== "admin")) return true;
  return session.dmSession !== true;
}
